import { Box, Flex, Link, Text, VStack } from '@chakra-ui/react';
import {
  ICheckResult,
  IGenericOccurrence
} from '@publishing-engine/domain/models';

import IntegrityStatusIcon from './IntegrityStatusIcon';
import CheckReportUrl from './CheckReportUrl';

type IProps = {
  check: ICheckResult;
  submissionId: string;
  submissionContextId: string;
  currentState: string;
};

type IGeneric = {
  occurrences: IGenericOccurrence;
};
type DataType =
  | 'TITLELIST'
  | 'TITLELINK'
  | 'LISTTEXT'
  | 'TITLETEXT'
  | 'TEXTS'
  | 'TITLESUBTITLE'
  | 'LISTLINK'
  | 'TITLELINKS'
  | 'UNKNOWN';

const TitleLink = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="actionSecondaryFill">
        {/* Name */}
        <Text fontWeight="medium" mb={2}>
          {occurrences.title}
        </Text>

        {/* Organization Section */}
        <Flex align="start" gap={2}>
          {occurrences.status && (
            <IntegrityStatusIcon severity={occurrences.status} />
          )}
          <Box>
            <Link
              href={`https://${occurrences.link?.href}`}
              color="blue.600"
              fontWeight="semibold"
              isExternal
            >
              {occurrences.link?.text}
            </Link>
            <Text fontSize="sm">{occurrences.text}</Text>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
const TitleLinks = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="actionSecondaryFill">
        <Flex align="start" gap={2}>
          {occurrences.status && (
            <IntegrityStatusIcon severity={occurrences.status} />
          )}
          <Box>{occurrences.text || occurrences.title}</Box>
        </Flex>
        <Box ml={'10'}>
          {Array.isArray(occurrences.link) &&
            occurrences?.link.map(link => {
              return (
                <Box my={1}>
                  <Link
                    href={`${link?.href}`}
                    color="blue.600"
                    fontWeight="semibold"
                    isExternal
                  >
                    {link?.text}
                  </Link>
                </Box>
              );
            })}
        </Box>
      </Box>
    </>
  );
};
const TitleList = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Flex
        align="start"
        gap={3}
        p={4}
        borderWidth="1px"
        borderRadius="md"
        bg="actionSecondaryFill"
      >
        {occurrences.status && (
          <IntegrityStatusIcon severity={occurrences.status} />
        )}
        <Box>
          <Text fontWeight="bold">{occurrences.title}</Text>
          {occurrences.list?.map(listitem => {
            return (
              <Text fontSize="sm" color="gray.600">
                {listitem.text}
              </Text>
            );
          })}
        </Box>
      </Flex>
    </>
  );
};
const TitleText = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="actionSecondaryFill">
        {/* Name */}
        <Text fontWeight="medium" mb={2}>
          {occurrences.title}
        </Text>

        {/* Organization Section */}
        <Flex align="start" gap={2}>
          {occurrences.status && (
            <IntegrityStatusIcon severity={occurrences.status} />
          )}
          <Box>
            <Text fontSize="sm">{occurrences.text}</Text>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
const ListText = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="actionSecondaryFill">
        <Flex align="start" gap={2}>
          {occurrences.status && (
            <IntegrityStatusIcon severity={occurrences.status} />
          )}
          <Box>
            {occurrences.list?.map(item => {
              return <Text>{item.text}</Text>;
            })}
          </Box>
        </Flex>
      </Box>
    </>
  );
};
const ListLink = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="actionSecondaryFill">
        {/* Name */}
        <Text fontWeight="medium" mb={2}>
          {occurrences.text}
        </Text>

        {/* Organization Section */}
        <Flex align="start" gap={2}>
          {occurrences.status && (
            <IntegrityStatusIcon severity={occurrences.status} />
          )}
          <Box>
            <Text fontSize="sm">{occurrences.link?.text}</Text>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
const Texts = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="actionSecondaryFill">
        <Flex align="start" gap={2}>
          {occurrences.status && (
            <IntegrityStatusIcon severity={occurrences.status} />
          )}
          <Box>{occurrences.text || occurrences.title}</Box>
        </Flex>
      </Box>
    </>
  );
};
const TitleSubtitle = ({ occurrences }: IGeneric) => {
  return (
    <>
      <Flex
        align="start"
        gap={3}
        p={4}
        borderWidth="1px"
        borderRadius="md"
        bg="actionSecondaryFill"
      >
        {occurrences.status && (
          <IntegrityStatusIcon severity={occurrences.status} />
        )}
        <Box>
          <Text fontWeight="bold">{occurrences.title}</Text>
          <Text fontSize="sm" color="gray.600">
            {occurrences.subtitle}
          </Text>
        </Box>
      </Flex>
    </>
  );
};
const Components: React.FC<{ check: IGenericOccurrence }> = ({ check }) => {
  function detectDataType(items: IGenericOccurrence): DataType {
    const item = items;
    if (item?.title && item.subtitle) {
      return 'TITLESUBTITLE';
    }

    if (item?.title && item.list) {
      return 'TITLELIST';
    }
    if (item.title && Array.isArray(item.link)) {
      return 'TITLELINKS';
    }
    if (item?.title && item.link) {
      return 'TITLELINK';
    }
    if (item.title && item.text) {
      return 'TITLETEXT';
    }

    if (item?.list) {
      return 'LISTTEXT';
    }
    if (item.link) {
      return 'LISTLINK';
    }

    if (item?.text) {
      return 'TEXTS';
    }
    if (item.title) {
      return 'TEXTS';
    }
    return 'UNKNOWN';
  }

  const type = detectDataType(check);

  switch (type) {
    case 'TITLELIST':
      return <TitleList occurrences={check as IGenericOccurrence} />;
    case 'TITLELINK':
      return <TitleLink occurrences={check as IGenericOccurrence} />;
    case 'LISTTEXT':
      return <ListText occurrences={check as IGenericOccurrence} />;
    case 'TITLETEXT':
      return <TitleText occurrences={check as IGenericOccurrence} />;
    case 'TEXTS':
      return <Texts occurrences={check as IGenericOccurrence} />;
    case 'TITLESUBTITLE':
      return <TitleSubtitle occurrences={check as IGenericOccurrence} />;
    case 'LISTLINK':
      return <ListLink occurrences={check as IGenericOccurrence} />;
    case 'TITLELINKS':
      return <TitleLinks occurrences={check as IGenericOccurrence} />;
    default:
      return <ListText occurrences={check as IGenericOccurrence} />;
  }
};
function CheckOccurrences({
  check,
  submissionId,
  submissionContextId,
  currentState
}: IProps) {
  return (
    <Box paddingLeft={8}>
      <Flex width={'full'} marginY={2} justify={'space-between'}>
        {check.result?.showDetailedReport && (
          <Box marginLeft={'auto'}>
            <CheckReportUrl
              submissionId={submissionId}
              checkType={check.checkType}
              runId={check.runId}
              submissionContextId={submissionContextId}
              currentState={currentState}
            />
          </Box>
        )}
      </Flex>
      <VStack spacing={4} align="stretch">
        {check.result?.occurrences?.map(item => {
          return <Components check={item} />;
        })}
      </VStack>
    </Box>
  );
}

export default CheckOccurrences;
